import React, { useState, useContext } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Alert, Platform } from 'react-native';
import { Text, Card, Title, Paragraph, List, Divider, FAB, Portal, Icon, useTheme, Button, Dialog, ActivityIndicator, Snackbar } from 'react-native-paper';
import { RouteProp, useRoute } from '@react-navigation/native';
import { FirebaseTripsContext } from '../context/FirebaseTripsContext';
import { RootStackParamList } from '../navigation/RootNavigator';
import { formatDate, formatTime } from '../utils/dateUtils';
import { Activity, Trip, ItineraryItem, HotelDetails } from '../types';
import ShareTripModal from './ShareTripModal';
import { useActivities } from '../hooks/useActivities';
import { isValid, parseISO } from 'date-fns';
import { firestore } from '../services/firebase';
import { doc, deleteDoc } from 'firebase/firestore';

type TripDetailRouteProp = RouteProp<RootStackParamList, 'TripDetail'>;

const toDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  if (timestamp.toDate) return timestamp.toDate();
  if (timestamp instanceof Date && isValid(timestamp)) return timestamp;
  if (typeof timestamp === 'string') {
    const d = parseISO(timestamp);
    if (isValid(d)) return d;
  }
  return null;
};

const formatDateTime = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  return `${formatDate(date)} ${formatTime(date)}`;
};

const DetailRow = ({ icon, label, value }: { icon: string, label: string, value?: string | null }) => {
  if (!value) return null;
  return (
    <View style={styles.detailRow}>
      <Icon source={icon} size={20} />
      <View style={styles.detailRowContent}>
        <Text variant="labelMedium">{label}</Text>
        <Text variant="bodyLarge">{value}</Text>
      </View>
    </View>
  );
};

const ActivityDetailContent = ({ activity }: { activity: ItineraryItem }) => {
  const renderFullAddress = (address?: HotelDetails['hotel_address']) => {
    if (!address) return null;
    return [address.street, address.city, address.postal_code, address.country].filter(Boolean).join(', ');
  };

  return (
    <ScrollView>
      <DetailRow icon="ticket-confirmation" label="Confirmation #" value={activity.confirmationNumber} />
      
      {/* Flight Details */}
      {activity.type === 'flight' && activity.flightDetails && (
        <>
          <Divider style={styles.divider} />
          <DetailRow icon="airplane" label="Airline" value={activity.flightDetails.airline} />
          <DetailRow icon="airplane-takeoff" label="Flight Number" value={activity.flightDetails.flightNumber} />
          <DetailRow icon="airport" label="From" value={activity.flightDetails.departureAirport} />
          <DetailRow icon="clock-outline" label="Departure" value={activity.flightDetails.departureTime ? formatDateTime(toDate(activity.flightDetails.departureTime)) : undefined} />
          <DetailRow icon="airport" label="To" value={activity.flightDetails.arrivalAirport} />
          <DetailRow icon="clock-check-outline" label="Arrival" value={activity.flightDetails.arrivalTime ? formatDateTime(toDate(activity.flightDetails.arrivalTime)) : undefined} />
          <DetailRow icon="seat-outline" label="Seat" value={activity.flightDetails.seat} />
          <DetailRow icon="barcode" label="Booking Reference" value={activity.flightDetails.bookingReference} />
        </>
      )}
      
      {/* Train Details */}
      {activity.type === 'train' && activity.trainDetails && (
        <>
          <Divider style={styles.divider} />
          <DetailRow icon="train" label="Operator" value={activity.trainDetails.trainOperator} />
          <DetailRow icon="train-variant" label="Train Number" value={activity.trainDetails.trainNumber} />
          <DetailRow icon="map-marker" label="From" value={activity.trainDetails.departureStation || activity.trainDetails.departure?.station} />
          <DetailRow icon="clock-outline" label="Departure" value={
            activity.trainDetails.departureTime ? 
            formatDateTime(toDate(activity.trainDetails.departureTime)) : 
            activity.trainDetails.departure?.datetime ? 
            formatDateTime(toDate(activity.trainDetails.departure.datetime)) : 
            undefined
          } />
          <DetailRow icon="map-marker-check" label="To" value={activity.trainDetails.arrivalStation || activity.trainDetails.arrival?.station} />
          <DetailRow icon="clock-check-outline" label="Arrival" value={
            activity.trainDetails.arrivalTime ? 
            formatDateTime(toDate(activity.trainDetails.arrivalTime)) : 
            activity.trainDetails.arrival?.datetime ? 
            formatDateTime(toDate(activity.trainDetails.arrival.datetime)) : 
            undefined
          } />
          <DetailRow icon="seat-outline" label="Seat" value={activity.trainDetails.seat} />
          <DetailRow icon="train-car" label="Carriage" value={activity.trainDetails.carriage} />
          <DetailRow icon="barcode" label="Booking Reference" value={activity.trainDetails.bookingReference} />
        </>
      )}
      
      {/* Bus Details */}
      {activity.type === 'bus' && activity.busDetails && (
        <>
          <Divider style={styles.divider} />
          <DetailRow icon="bus" label="Operator" value={activity.busDetails.operator} />
          <DetailRow icon="bus-side" label="Line Number" value={activity.busDetails.lineNumber} />
          <DetailRow icon="map-marker" label="From" value={activity.busDetails.departureStation || activity.busDetails.departure?.station} />
          <DetailRow icon="clock-outline" label="Departure" value={
            activity.busDetails.departureTime ? 
            formatDateTime(toDate(activity.busDetails.departureTime)) : 
            activity.busDetails.departure?.datetime ? 
            formatDateTime(toDate(activity.busDetails.departure.datetime)) : 
            undefined
          } />
          <DetailRow icon="map-marker-check" label="To" value={activity.busDetails.arrivalStation || activity.busDetails.arrival?.station} />
          <DetailRow icon="clock-check-outline" label="Arrival" value={
            activity.busDetails.arrivalTime ? 
            formatDateTime(toDate(activity.busDetails.arrivalTime)) : 
            activity.busDetails.arrival?.datetime ? 
            formatDateTime(toDate(activity.busDetails.arrival.datetime)) : 
            undefined
          } />
          <DetailRow icon="seat-outline" label="Seat" value={activity.busDetails.seat} />
          <DetailRow icon="barcode" label="Booking Reference" value={activity.busDetails.bookingReference} />
        </>
      )}
      
      {/* Hotel Details */}
      {activity.type === 'hotel' && activity.hotelDetails && (
        <>
          <Divider style={styles.divider} />
          <DetailRow icon="office-building" label="Hotel" value={activity.hotelDetails.hotel_name} />
          <DetailRow icon="calendar-start" label="Check-in" value={formatDate(toDate(activity.hotelDetails.check_in_date))} />
          <DetailRow icon="calendar-end" label="Check-out" value={formatDate(toDate(activity.hotelDetails.check_out_date))} />
          <DetailRow icon="bed" label="Room" value={activity.hotelDetails.room_description} />
          <DetailRow icon="map-marker" label="Address" value={renderFullAddress(activity.hotelDetails.hotel_address)} />
        </>
      )}
      
      {/* Transport Details */}
      {activity.type === 'transport' && activity.transportDetails && (
        <>
          <Divider style={styles.divider} />
          <DetailRow icon="car" label="Provider" value={activity.transportDetails.provider} />
          <DetailRow icon="car-info" label="Vehicle Type" value={activity.transportDetails.vehicleType} />
          <DetailRow icon="map-marker-plus" label="Pickup" value={activity.transportDetails.pickupLocation} />
          <DetailRow icon="map-marker-minus" label="Dropoff" value={activity.transportDetails.dropoffLocation} />
          <DetailRow icon="ticket" label="Confirmation" value={activity.transportDetails.confirmationNumber} />
        </>
      )}
      
      {/* Additional Info */}
      {activity.notes && (
        <>
          <Divider style={styles.divider} />
          <Text style={styles.notesLabel}>Notes</Text>
          <Paragraph>{activity.notes}</Paragraph>
        </>
      )}
      
      {/* Source & Confidence */}
      {activity.source === 'email' && (
        <>
          <Divider style={styles.divider} />
          <DetailRow icon="email" label="Source" value="Email Import" />
          {activity.aiConfidence && (
            <DetailRow 
              icon="robot" 
              label="AI Confidence" 
              value={`${Math.round(activity.aiConfidence * 100)}%`} 
            />
          )}
        </>
      )}
    </ScrollView>
  );
};

const ActivitySummary = ({ activity }: { activity: ItineraryItem }) => {
  switch (activity.type) {
    case 'hotel':
      const hotel = activity.hotelDetails;
      const checkIn = toDate(hotel?.check_in_date);
      const checkOut = toDate(hotel?.check_out_date);
      return (
        <View>
          <Text>{hotel?.hotel_name || activity.location}</Text>
          {checkIn && checkOut && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              Check-in: {formatDate(checkIn)} • Check-out: {formatDate(checkOut)}
            </Text>
          )}
          {activity.confirmationNumber && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              Confirmation: {activity.confirmationNumber}
            </Text>
          )}
        </View>
      );
    case 'flight':
      const flight = activity.flightDetails;
      const departureTime = toDate(flight?.departureTime);
      const arrivalTime = toDate(flight?.arrivalTime);
      return (
        <View>
          <Text>
            {flight?.airline} {flight?.flightNumber || activity.title}
          </Text>
          {flight?.departureAirport && flight?.arrivalAirport && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              {flight.departureAirport} → {flight.arrivalAirport}
            </Text>
          )}
          {departureTime && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              Departs: {formatDateTime(departureTime)}
              {flight?.seat && ` • Seat: ${flight.seat}`}
            </Text>
          )}
        </View>
      );
    case 'train':
      const train = activity.trainDetails;
      const trainDepartureTime = toDate(train?.departureTime || train?.departure?.datetime);
      const trainArrivalTime = toDate(train?.arrivalTime || train?.arrival?.datetime);
      return (
        <View>
          <Text>
            {train?.trainOperator} {train?.trainNumber || activity.title}
          </Text>
          {(train?.departureStation || train?.departure?.station) && (train?.arrivalStation || train?.arrival?.station) && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              {train?.departureStation || train?.departure?.station} → {train?.arrivalStation || train?.arrival?.station}
            </Text>
          )}
          {trainDepartureTime && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              Departs: {formatDateTime(trainDepartureTime)}
              {train?.seat && ` • Seat: ${train.seat}`}
              {train?.carriage && ` • Car: ${train.carriage}`}
            </Text>
          )}
        </View>
      );
    case 'bus':
      const bus = activity.busDetails;
      const busDepartureTime = toDate(bus?.departureTime || bus?.departure?.datetime);
      const busArrivalTime = toDate(bus?.arrivalTime || bus?.arrival?.datetime);
      return (
        <View>
          <Text>
            {bus?.operator} {bus?.lineNumber || activity.title}
          </Text>
          {(bus?.departureStation || bus?.departure?.station) && (bus?.arrivalStation || bus?.arrival?.station) && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              {bus?.departureStation || bus?.departure?.station} → {bus?.arrivalStation || bus?.arrival?.station}
            </Text>
          )}
          {busDepartureTime && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              Departs: {formatDateTime(busDepartureTime)}
              {bus?.seat && ` • Seat: ${bus.seat}`}
            </Text>
          )}
        </View>
      );
    case 'transport':
      const transport = activity.transportDetails;
      return (
        <View>
          <Text>{transport?.provider || activity.title}</Text>
          {transport?.pickupLocation && transport?.dropoffLocation && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              {transport.pickupLocation} → {transport.dropoffLocation}
            </Text>
          )}
          {transport?.confirmationNumber && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              Confirmation: {transport.confirmationNumber}
            </Text>
          )}
        </View>
      );
    default:
      return (
        <View>
          <Text>{activity.location}</Text>
          {activity.confirmationNumber && (
            <Text style={{ fontSize: 12, color: '#666' }}>
              Confirmation: {activity.confirmationNumber}
            </Text>
          )}
        </View>
      );
  }
};

export default function TripDetailScreen() {
  const route = useRoute<TripDetailRouteProp>();
  const tripsContext = useContext(FirebaseTripsContext);
  const theme = useTheme();
  const [selectedActivity, setSelectedActivity] = useState<ItineraryItem | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const trip = tripsContext?.allTrips.find(t => t.id === route.params.tripId);
  const { groupedActivities, isLoading } = useActivities(trip?.id);

  if (!trip) return <View style={styles.centered}><Text>Trip not found</Text></View>;

  const getActivityIcon = (type?: Activity['type']) => {
    switch (type) {
      case 'hotel': return 'bed';
      case 'flight': return 'airplane';
      case 'train': return 'train';
      case 'bus': return 'bus';
      case 'transport': return 'car';
      case 'restaurant': return 'food';
      case 'activity': return 'ticket';
      default: return 'calendar';
    }
  };

  const handleDeleteActivity = () => {
    if (Platform.OS === 'web') {
      setShowDeleteDialog(true);
    } else {
      Alert.alert(
        "Delete Activity",
        "Are you sure you want to delete this activity?",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Delete",
            style: "destructive",
            onPress: async () => {
              await performDelete();
            }
          }
        ]
      );
    }
  };

  const performDelete = async () => {
    if (!selectedActivity || !trip?.id) return;
    
    try {
      console.log("Deleting activity:", selectedActivity.id);
      await deleteDoc(doc(firestore, "trips", trip.id, "activities", selectedActivity.id));
      setSnackbarMessage("Activity deleted");
      setSelectedActivity(null);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Error deleting activity:", error);
      if (Platform.OS === 'web') {
        setSnackbarMessage("Error: Could not delete activity.");
      } else {
        Alert.alert("Error", "Could not delete activity.");
      }
    }
  };

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.headerCard}>
        <Card.Cover source={{ uri: trip.coverImage || 'https://picsum.photos/700' }} />
        <Card.Content><Title>{trip.name}</Title></Card.Content>
      </Card>

      <View style={styles.itineraryContainer}>
        <Title>Itinerary</Title>
        {isLoading ? <ActivityIndicator /> : groupedActivities.length === 0 ? (
          <Text style={{ textAlign: 'center', marginTop: 20, color: '#666' }}>No activities yet. Add emails from your inbox to see them here.</Text>
        ) : groupedActivities.map((day, i) => (
          <View key={i}>
            <Text style={styles.dayHeader}>{formatDate(day.date)}</Text>
            <Divider />
            {day.activities.map((activity) => (
              <TouchableOpacity key={activity.id} onPress={() => setSelectedActivity(activity)}>
                <List.Item
                  title={activity.title || 'Activity'}
                  description={() => <ActivitySummary activity={activity} />}
                  left={props => <List.Icon {...props} icon={getActivityIcon(activity.type)} />}
                  right={props => (
                    <TouchableOpacity 
                      onPress={(e) => {
                        e.stopPropagation();
                        setSelectedActivity(activity);
                        handleDeleteActivity();
                      }}
                      style={{ padding: 8 }}
                    >
                      <Icon source="delete" size={24} color={theme.colors.error} />
                    </TouchableOpacity>
                  )}
                />
              </TouchableOpacity>
            ))}
          </View>
        ))}
      </View>

      <Portal>
        <Dialog visible={!!selectedActivity} onDismiss={() => setSelectedActivity(null)}>
          <Dialog.Title>{selectedActivity?.title}</Dialog.Title>
          <Dialog.Content>
            {selectedActivity && <ActivityDetailContent activity={selectedActivity} />}
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={handleDeleteActivity} textColor={theme.colors.error}>Delete</Button>
            <Button onPress={() => setSelectedActivity(null)}>Close</Button>
          </Dialog.Actions>
        </Dialog>
        
        <Dialog visible={showDeleteDialog} onDismiss={() => setShowDeleteDialog(false)}>
          <Dialog.Title>Delete Activity</Dialog.Title>
          <Dialog.Content>
            <Paragraph>Are you sure you want to delete this activity?</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button onPress={performDelete} textColor={theme.colors.error}>Delete</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
      
      <Snackbar visible={!!snackbarMessage} onDismiss={() => setSnackbarMessage("")}>{snackbarMessage}</Snackbar>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  headerCard: { margin: 16 },
  itineraryContainer: { padding: 16 },
  dayHeader: { fontSize: 18, fontWeight: 'bold', paddingVertical: 8 },
  detailRow: { flexDirection: 'row', alignItems: 'center', marginVertical: 4 },
  detailRowContent: { marginLeft: 8 },
  detailLabel: { fontSize: 12, color: '#888' },
  divider: { marginVertical: 8 },
  notesLabel: { fontSize: 14, fontWeight: 'bold', marginBottom: 4, color: '#666' },
});